import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  RealtorRatingBodyLayout as Layout,
  RealtorRatingFieldPeriod as FieldPeriod,
  RealtorRatingFieldRegion as FieldRegion,
  RealtorRatingHeader as Header,
  RealtorRatingTableThead as TableThead,
  RealtorRatingTableTbody as TableTbody,
  RealtorRatingTableTbodySkeleton as TableTbodySkeleton,
  RealtorRatingTableTbodyNotFound as TableTbodyNotFound,
} from '../../components/RealtorRating';
import {
  getRealtorRatingPeriodFieldOptions,
  getRealtorRatingRegionFieldOptions,
  useGetRealtorRatingListing,
  useRealtorRatingFilter,
  useRealtorRatingSortOrdering,
  useServerNow,
} from '../../utils';
import { selectRegionsNew } from '../../selectors/geo/regionsNew';
import { selectRealtorRatingList } from '../../selectors/realtorRatingList';
import { getRealtorRatingList, resetRealtorRatingList } from '../../actions/realtorRatingList';
import { ERequestStatus } from '../../types/requestStatus';
import { REALTOR_RATING_DEFAULT_PERIOD_VALUE } from '../../constants';
import { TThunkDispatch } from '../../types/redux';

/**
 * Контейнер листинга рейтинга риелторов
 */
export const RealtorRatingContainer = () => {
  const dispatch = useDispatch<TThunkDispatch>();
  const serverNow = useServerNow();
  const { sortOrdering, sortOrderingType, setSortOrdering } = useRealtorRatingSortOrdering();
  const { region, setRegion, period, setPeriod, agentId } = useRealtorRatingFilter();
  const { items: regionItems } = useSelector(selectRegionsNew);
  const { items: realtorRatingListItems, status: realtorRatingListStatus } = useSelector(selectRealtorRatingList);

  /** Забирает список в стор и трекает показ листинга */
  useGetRealtorRatingListing(() => {
    dispatch(
      getRealtorRatingList({
        region,
        period,
        agentId,
      }),
    ).finally();
  });

  /** При выходе сбрасывает список */
  useEffect(() => {
    return () => {
      dispatch(resetRealtorRatingList());
    };
  }, [dispatch]);

  return (
    <Layout
      header={<Header />}
      filterFields={
        <>
          <FieldRegion
            value={region}
            options={getRealtorRatingRegionFieldOptions(regionItems)}
            onChange={setRegion}
            disabled={realtorRatingListStatus === ERequestStatus.Loading}
          />

          <FieldPeriod
            value={period}
            options={getRealtorRatingPeriodFieldOptions(serverNow.getFullYear())}
            onChange={setPeriod}
            disabled={realtorRatingListStatus === ERequestStatus.Loading}
          />
        </>
      }
      tableRows={
        <>
          <TableThead
            onSort={realtorRatingListStatus !== ERequestStatus.Loading ? setSortOrdering : undefined}
            sortOrdering={sortOrdering}
            sortOrderingType={sortOrderingType}
          />

          {realtorRatingListStatus === ERequestStatus.Loading ? (
            <TableTbodySkeleton />
          ) : realtorRatingListItems.length ? (
            <TableTbody
              items={realtorRatingListItems}
              sortOrdering={sortOrdering}
              sortOrderingType={sortOrderingType}
              agentId={agentId}
            />
          ) : (
            <TableTbodyNotFound
              showResetFiltersLink={Boolean(region !== 0 || period !== REALTOR_RATING_DEFAULT_PERIOD_VALUE)}
            />
          )}
        </>
      }
    />
  );
};
